import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

const RETURN_URL_KEY = 'postLoginReturnUrl';
const EXCLUDED_PATHS  = ['/login', '/auth-error'];

@Injectable({ providedIn: 'root' })
export class SessionReloginService {
  private readonly router = inject(Router);

  // Guard: ensures only one session-expired flow fires even if many 401s arrive at once
  private triggered = false;

  /** Save the current URL and navigate to /login?reason=expired.
   *  Safe to call multiple times — only the first call fires. */
  trigger(): void {
    if (this.triggered) return;
    this.triggered = true;

    const url = this.router.url;
    if (url && !EXCLUDED_PATHS.some(p => url.startsWith(p))) {
      localStorage.setItem(RETURN_URL_KEY, url);
    }

    void this.router.navigate(['/login'], { queryParams: { reason: 'expired' } });
  }

  /** After a successful login, navigate to the saved URL (or /dashboard as fallback). */
  restoreAndNavigate(): void {
    const saved = this.popReturnUrl();
    void this.router.navigateByUrl(saved ?? '/dashboard');
  }

  /** Read the stored return URL without removing it. */
  getReturnUrl(): string | null {
    return localStorage.getItem(RETURN_URL_KEY);
  }

  /** Read and remove the stored return URL. */
  private popReturnUrl(): string | null {
    const url = localStorage.getItem(RETURN_URL_KEY);
    if (url) localStorage.removeItem(RETURN_URL_KEY);
    return url;
  }

  /** Reset the guard so a future session expiry can trigger the flow again. */
  reset(): void {
    this.triggered = false;
  }
}
